import styled from 'styled-components';
import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '@companycam/slab-web';
import { TemplateModalContext } from '../TemplateModalContext';

import { graphQLIdDecoder } from 'helpers/graphql-utils';
import { ADMIN, MANAGER, hasRoleAccess } from 'helpers/roles';
import { previousView, setCurrentTemplateId } from 'actions/todos';
import { useCurrentUser } from 'javascripts/hooks';
import { translate } from 'javascripts/translations/i18n';
import { useBackwardsCompatibleNavigate } from 'components/navigation/useBackwardsCompatibleNavigation';

// #region styled-components

const RightButtons = styled.div`
  display: flex;
  gap: 12px;
`;
const ButtonDiv = styled.div`
  justify-self: flex-start;
  flex: 1;
  display: flex;
  padding-left: 8px;
`;

// #endregion

type FooterButtonsProps = {
  createTaskListFromTemplate: () => void;
  isLoading: boolean;
  ownsTemplate: boolean;
  onRequestClose?: () => void;
};

const FooterButtons = ({
  createTaskListFromTemplate,
  isLoading,
  ownsTemplate,
  onRequestClose,
}: FooterButtonsProps) => {
  const navigate = useBackwardsCompatibleNavigate();
  const dispatch = useDispatch();
  const { projectNodeId } = useContext(TemplateModalContext);
  const { databaseId: projectId } = graphQLIdDecoder(projectNodeId);
  const user = useCurrentUser();
  const hasManagePermissions = hasRoleAccess(user, [ADMIN, MANAGER]);

  const currentTemplateId = useSelector(
    ({ todos }: any) => todos.templates.currentTemplateId,
  );

  const navigateToEditTemplate = () => {
    onRequestClose();
    navigate(
      `/todos/templates/edit/${currentTemplateId}?projectId=${projectId}`,
    );
  };

  return (
    <>
      <RightButtons>
        {hasManagePermissions && ownsTemplate ? (
          <Button
            color="subtle"
            icon={{
              name: 'pencil',
              position: 'before',
            }}
            onClick={navigateToEditTemplate}
            aria-label={translate('projects.taskList.templates.edit')}
          >
            {translate('projects.taskList.templates.edit')}
          </Button>
        ) : null}
        <Button
          color="primary"
          icon={{
            name: 'plus',
            position: 'before',
          }}
          onClick={createTaskListFromTemplate}
          disabled={isLoading}
          loading={isLoading}
          aria-label={translate('projects.taskList.templates.useTemplate')}
        >
          {translate('projects.taskList.templates.useTemplate')}
        </Button>
      </RightButtons>
      {hasManagePermissions && ownsTemplate ? (
        <ButtonDiv>
          <Button
            color="subtle"
            design="minimal"
            onClick={() => {
              dispatch(setCurrentTemplateId(null));
              dispatch(previousView());
            }}
            aria-label={translate('common.cancel')}
          >
            {translate('common.cancel')}
          </Button>
        </ButtonDiv>
      ) : (
        <Button
          color="subtle"
          onClick={() => {
            dispatch(setCurrentTemplateId(null));
            dispatch(previousView());
          }}
          aria-label={translate('common.cancel')}
        >
          {translate('common.cancel')}
        </Button>
      )}
    </>
  );
};

export default FooterButtons;
